import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import formConfig from './formConfig';
import TabNavigation from './TabNavigation';
import FormTab from './FormTab';
import ReviewTab from './ReviewTab';
import './styles.css';
import { useNavigate } from "react-router-dom";
import { createPatientDetails } from '../../Services/APIs/patient';
import { postLogDetails } from '../../Services/APIs/appDetails';
import { useDispatch } from "react-redux";
import { addPatientData } from "../../Store/patientKyc";
import { useFormPersistence } from '../../hooks/useFormPersistence';

const generateInitialValues = () => {
  const initial = {};
  formConfig.tabs.forEach((tab) => {
    tab.fields.forEach((field) => {
      if (field.type === 'checkbox') {
        initial[field.name] = false;
      } else if (field.type === 'multiselect') {
        initial[field.name] = [];
      }
      else {
        initial[field.name] = '';
      }
    });
  });
  return initial;
};


const DentalInsuranceForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [validationSchemas, setValidationSchemas] = useState([]);
  const [validationError, setValidationError] = useState(false);
  const navigate = useNavigate()
  const [navigateTo, setNavigateTo] = useState(null);
  const dispatch = useDispatch()
  
  // Use form persistence hook
  const { formData, setFormData, clearFormData } = useFormPersistence(generateInitialValues())

  useEffect(() => {
    // postLogDetails({
    //   uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
    //   app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
    //   payLoad: `form submitted: ${navigateTo}`

    // })
    if (navigateTo) {
      // postLogDetails({
      //   uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
      //   app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
      //   payLoad: `form submitted: ${navigateTo}`

      // })
      navigate(navigateTo);
    }
  }, [navigateTo]);
  useEffect(() => {
    const schemas = formConfig.tabs.map(tab => {
      const schemaObj = {};
      tab.fields.forEach(field => {
        if (field.required) {
          if (field.type === 'text' || field.type === 'textarea') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'email') {
            schemaObj[field.name] = Yup.string().email('Invalid email format').required(`${field.label} is required`);
          } else if (field.type === 'date') {
            schemaObj[field.name] = Yup.date().required(`${field.label} is required`);
          } else if (field.type === 'checkbox') {
            schemaObj[field.name] = Yup.boolean().oneOf([true], `${field.label} must be accepted`);
          } else if (field.type === 'radio' || field.type === 'select') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'fileUpload') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'multiselect') {
            schemaObj[field.name] = Yup.array().min(1, `${field.label} is required`).required(`${field.label} is required`);
          } else if (field.name === 'declarationAgreement') {
            schemaObj[field.name] = Yup.boolean().oneOf([true], 'You must accept the declaration to proceed');
          } else if (field.type === 'number') {
            schemaObj[field.name] = Yup.number()
              .typeError(`${field.label} must be a number`)
              .required(`${field.label} is required`)
              .test(
                'len',
                `${field.label} must be at most 10 digits`,
                (val) => val === undefined || val.toString().length <= 10
              );
          }



        }
      });
      return Yup.object().shape(schemaObj);
    });
    setValidationSchemas(schemas);
  }, []);

  const handleNextTab = (values, actions) => {
    actions.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        setFormData({ ...formData, ...values });
        if (activeTab < formConfig.tabs.length - 1) {
          setActiveTab(activeTab + 1);
          setValidationError(false);
        }
        actions.setSubmitting(false);
      } else {
        const touchedFields = {};
        Object.keys(errors).forEach((field) => {
          touchedFields[field] = true;
        });
        actions.setTouched(touchedFields);
        setValidationError(true);
        actions.setSubmitting(false);
      }
    });
  };

  const handlePrevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
      setValidationError(false);
    }
  };
  // Utility: Convert base64 string to File
  const base64ToFile = (base64Data, fileName, mimeType = "image/jpeg") => {
    const byteString = atob(base64Data.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], fileName, { type: mimeType });
  };

  const handleSubmit = async (values) => {
    // console.log(values, "submitted values");

    const { aadhaarCardBack, aadhaarCard, panCard, ...restValues } = values;
    const formData = new FormData();
    // console.log(typeof aadhaarCard === "string", aadhaarCard.startsWith("data:image"))
    // Convert base64 to File and append
    if (typeof aadhaarCard === "string" && aadhaarCard.startsWith("data:image")) {
      const aadhaarFile = base64ToFile(aadhaarCard, "aadhaar.jpg");
      formData.append("aadhaarCard", aadhaarFile);
    }

    if (typeof aadhaarCardBack === "string" && aadhaarCardBack.startsWith("data:image")) {
      const aadhaarBackFile = base64ToFile(aadhaarCardBack, "aadhaar_back.jpg");
      formData.append("aadhaarCardBack", aadhaarBackFile);
    }

    if (typeof panCard === "string" && panCard.startsWith("data:image")) {
      const panFile = base64ToFile(panCard, "pan.jpg");
      formData.append("panCard", panFile);
    }

    // Append all other form fields
    for (const key in restValues) {
      if (restValues[key] !== undefined && restValues[key] !== null) {
        formData.append(key, restValues[key]);
      }
    }

    // Add UID and company from localStorage
    const uid = JSON.parse(localStorage.getItem("usertpi"))?.usertpi;
    const company = JSON.parse(localStorage.getItem("companyBucket"))?.companyBucket;
    formData.append("uid", uid);
    formData.append("company", company);

    // Optional logging
    // console.log([...formData.entries()]);
    // for (const [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }
    try {
      // await createPatientDetails(formData); // Must accept FormData (multipart/form-data)
      dispatch(addPatientData(values));
      postLogDetails({
        uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
        app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
        payLoad: `patient kyc state updated success`
      })
    } catch (err) {
      postLogDetails({
        uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
        app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
        payLoad: `patient kyc state updated failure`
      })
    }
    const cameraside = localStorage.getItem("cameraSide")
    const companyBucketMap = {
      "ab": "ab", // "aditya-birlaBucket",
      "tl-star": "starhealth",     // "Starhealth"
      "care": "care",  //Care Insurance
      "plum": "pluminsurance"
    };
    const validCameraSides = ["front", "back"];
    const resolvedCameraSide = validCameraSides.includes(cameraside) ? cameraside : "front";
    const resolvedCompany = companyBucketMap.hasOwnProperty(company) ? company : "tl-star";

    navigate(`/${resolvedCompany}/${resolvedCameraSide}/scan`)

  };




  const isLastTab = activeTab === formConfig.tabs.length - 1;
  const isReviewTab = activeTab === formConfig.tabs.length - 1;

  return (
    <div className="dental-insurance-form">
      <h1 className="form-title">Dental Insurance Enrollment</h1>

      {validationError && (
        <div className="validation-error-message">
          Please fill all required fields before proceeding to the next step.
        </div>
      )}

      <Formik
        initialValues={formData}
        validationSchema={validationSchemas[activeTab]}
        onSubmit={isLastTab ? handleSubmit : handleNextTab}
        enableReinitialize
        validateOnChange
        validateOnBlur
      >
        {(formikProps) => {
          const {
            values,
            setFieldValue,
            errors,
            touched,
            validateForm,
            setTouched,
            isSubmitting
          } = formikProps;

          return (
            <>
              <Form className="form-container">
                {isReviewTab ? (
                  <ReviewTab
                    formData={formData}
                    formConfig={formConfig}
                    {...formikProps}
                  />
                ) : (
                  <FormTab
                    key={activeTab}
                    tabConfig={formConfig.tabs[activeTab]}
                    formikProps={{ values, setFieldValue, errors, touched }}
                  />
                )}

                <div className="form-navigation">
                  {activeTab > 0 && (
                    <button
                      type="button"
                      className="btn-prev"
                      onClick={handlePrevTab}
                    >
                      Previous
                    </button>
                  )}

                  <button
                    type="submit"
                    className="btn-next"
                    disabled={isSubmitting}
                  >
                    {isLastTab ? 'Confirm' : 'Next'}
                  </button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default DentalInsuranceForm;
